import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import api, { apiError, notifyChange, STATUSES } from "../../lib/api";
import { toast } from "sonner";
import PatientSelect from "../PatientSelect";
import { CalendarClock, LogIn, LogOut, RotateCcw, Trash2, Copy, ClipboardPaste } from "lucide-react";

const DURATIONS = [15, 20, 30, 45, 60, 90, 120];

export default function AppointmentModal({ open, onClose, prefill, onSaved }) {
  const editing = prefill?.appointment || null;
  const medicalSlot = !editing ? prefill?.medicalSlot || null : null;
  const medicalEditing = editing?.booking_mode === "medical_slot";
  const isMedical = Boolean(medicalSlot || medicalEditing);

  const [patient, setPatient] = useState(null);
  const [date, setDate] = useState(prefill?.date || new Date().toISOString().slice(0, 10));
  const [start, setStart] = useState(prefill?.start || "09:00");
  const [duration, setDuration] = useState(45);
  const [treatmentId, setTreatmentId] = useState("");
  const [physioId, setPhysioId] = useState("");
  const [machineId, setMachineId] = useState("");
  const [autoAssign, setAutoAssign] = useState(false);
  const [notes, setNotes] = useState("");
  const [price, setPrice] = useState("");
  const [payStatus, setPayStatus] = useState("da_pagare");
  const [payMethod, setPayMethod] = useState("contanti");
  const [treatments, setTreatments] = useState([]);
  const [avail, setAvail] = useState({ physios: [], machines: [] });
  const [saving, setSaving] = useState(false);
  const [moveDate, setMoveDate] = useState("");
  const [moveOptions, setMoveOptions] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [copiedAppointment, setCopiedAppointment] = useState(() => {
    try {
      const raw = sessionStorage.getItem("ll:copied-appointment");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!open) return;
    api.get("/treatments").then(({ data }) => setTreatments(data.filter((t) => t.active !== false)));
    if (editing) {
      setPatient({ id: editing.patient_id, first_name: (editing.patient_name || "").split(" ")[0] || "", last_name: (editing.patient_name || "").split(" ").slice(1).join(" "), phone: "" });
      if (!editing.patient_name) setPatient(null);
      api.get(`/patients/${editing.patient_id}`).then(({ data }) => setPatient(data.patient)).catch(() => {});
      setDate(editing.date);
      setStart(editing.start);
      setMoveDate(editing.date);
      const [h1, m1] = editing.start.split(":").map(Number);
      const [h2, m2] = editing.end.split(":").map(Number);
      setDuration(h2 * 60 + m2 - (h1 * 60 + m1));
      setTreatmentId(editing.treatment_id || "");
      setPhysioId(editing.physio_id || "");
      setMachineId(editing.machine_id || "");
      setNotes(editing.notes || "");
      setPrice(editing.price || "");
      setPayStatus(editing.payment_status || "da_pagare");
      setPayMethod(editing.payment_method || "contanti");
      setAutoAssign(false);
    } else if (medicalSlot) {
      setPatient(null);
      setDate(medicalSlot.date);
      setStart(medicalSlot.start);
      setMoveDate("");
      setDuration(Number(medicalSlot.duration));
      setPhysioId(medicalSlot.physio_id);
      setTreatmentId("");
      setMachineId("");
      setNotes("");
      setPrice("");
      setPayStatus("da_pagare");
      setPayMethod("contanti");
      setAutoAssign(false);
    } else {
      setPatient(null); setTreatmentId(""); setPhysioId(""); setMachineId("");
      setNotes(""); setPrice(""); setPayStatus("da_pagare"); setPayMethod("contanti"); setAutoAssign(false);
      setMoveDate(""); setMoveOptions([]);
      setDate(prefill?.date || new Date().toISOString().slice(0, 10));
      setStart(prefill?.start || "09:00");
      setDuration(45);
    }
  }, [open, editing, medicalSlot, prefill?.date, prefill?.start]);

  useEffect(() => {
    if (!open || !date || !start) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/appointments/availability/check", {
          params: { date, start, duration, treatment_id: treatmentId || undefined, exclude_id: editing?.id },
        });
        setAvail(data);
      } catch { /* noop */ }
    }, 200);
    return () => clearTimeout(t);
  }, [open, date, start, duration, treatmentId, editing?.id]);

  useEffect(() => {
    const movable = medicalEditing && editing && !["annullato", "completato", "non_presentato"].includes(editing.status);
    if (!open || !movable || !moveDate || !editing?.physio_id) {
      setMoveOptions([]);
      return;
    }
    let active = true;
    setMoveLoading(true);
    api.get("/medical-availabilities/batch", {
      params: { date_from: moveDate, date_to: moveDate, physio_id: editing.physio_id },
    }).then(({ data }) => {
      if (!active) return;
      const options = [];
      (data.availabilities || [])
        .filter((a) => a.physio_id === editing.physio_id)
        .forEach((a) => {
          (a.grid?.slots || []).filter((s) => s.available).forEach((s) => options.push({
            availability_id: a.id,
            start: s.start,
            end: s.end,
            duration: a.slot_duration_minutes,
          }));
        });
      setMoveOptions(options);
    }).catch((e) => {
      if (active) {
        setMoveOptions([]);
        toast.error(apiError(e, "Impossibile caricare gli slot disponibili"));
      }
    }).finally(() => active && setMoveLoading(false));
    return () => { active = false; };
  }, [open, medicalEditing, moveDate, editing?.id, editing?.physio_id, editing?.status]);

  const onTreatment = (tid) => {
    setTreatmentId(tid);
    const t = treatments.find((x) => x.id === tid);
    if (t) {
      setPrice(String(t.price || ""));
      if (!isMedical) setDuration(t.duration_minutes || 45);
      if ((t.machine_type_ids?.length || t.machine_ids?.length) && !machineId) setAutoAssign(true);
      if (!(t.machine_type_ids?.length || t.machine_ids?.length)) { setMachineId(""); setAutoAssign(false); }
    }
  };

  const copyCurrentAppointment = () => {
    if (!editing) return;
    if (medicalEditing || editing?.booking_mode === "medical_slot") {
      toast.error("Gli appuntamenti medici si gestiscono tramite gli slot dedicati");
      return;
    }
    const payload = {
      id: editing.id,
      patient_name: editing.patient_name,
      start: editing.start,
      date: editing.date,
    };
    sessionStorage.setItem("ll:copied-appointment", JSON.stringify(payload));
    setCopiedAppointment(payload);
    toast.success(`Copiato: ${editing.patient_name} · ${editing.start}`);
  };

  const pasteCopiedAppointment = async () => {
    if (!copiedAppointment || editing || isMedical) return;
    setSaving(true);
    try {
      await api.post(`/appointments/${copiedAppointment.id}/copy`, {
        date,
        start,
      });
      toast.success("Appuntamento incollato");
      notifyChange();
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Impossibile incollare l'appuntamento"));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!patient) { toast.error("Seleziona un paziente"); return; }
    if (!isMedical && !physioId) { toast.error("Seleziona un fisioterapista"); return; }
    setSaving(true);
    try {
      if (medicalEditing) {
        await api.put(`/medical-appointments/${editing.id}`, {
          treatment_id: treatmentId || null,
          machine_id: autoAssign ? null : machineId || null,
          auto_assign_machine: autoAssign,
          notes,
          price: Number(price) || 0,
          payment_status: payStatus,
          payment_method: payMethod,
        });
      } else if (medicalSlot) {
        const body = {
          patient_id: patient.id,
          medical_availability_id: medicalSlot.availability_id,
          slot_start: medicalSlot.start,
          treatment_id: treatmentId || null,
          machine_id: autoAssign ? null : machineId || null,
          auto_assign_machine: autoAssign,
          notes,
          price: Number(price) || 0,
          payment_status: payStatus,
          payment_method: payMethod,
        };
        await api.post("/medical-appointments", body);
      } else {
        const body = {
          patient_id: patient.id, physio_id: physioId, date, start,
          duration: Number(duration), treatment_id: treatmentId || null,
          machine_id: autoAssign ? null : machineId || null,
          auto_assign_machine: autoAssign,
          notes, price: Number(price) || 0, payment_status: payStatus, payment_method: payMethod,
        };
        if (editing) await api.put(`/appointments/${editing.id}`, body);
        else await api.post("/appointments", body);
      }
      toast.success(editing ? "Appuntamento aggiornato" : isMedical ? "Slot medico prenotato" : "Appuntamento creato");
      notifyChange();
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(apiError(e, "Errore nel salvataggio"));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Eliminare questo appuntamento?")) return;
    try {
      await api.delete(`/appointments/${editing.id}`);
      toast.success("Appuntamento eliminato");
      notifyChange(); onSaved?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/appointments/${editing.id}/status`, { status, payment_status: payStatus, payment_method: payMethod });
      toast.success("Stato aggiornato");
      notifyChange(); onSaved?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const reactivate = async () => {
    try {
      await api.post(`/medical-appointments/${editing.id}/reactivate`);
      toast.success("Appuntamento riattivato");
      notifyChange(); onSaved?.(); onClose();
    } catch (e) { toast.error(apiError(e, "Slot non più disponibile")); }
  };

  const moveTo = async (slot) => {
    if (!window.confirm(`Spostare l'appuntamento al ${new Date(moveDate + "T12:00:00").toLocaleDateString("it-IT")} alle ${slot.start}?`)) return;
    setMoving(true);
    try {
      await api.put(`/medical-appointments/${editing.id}/move`, {
        medical_availability_id: slot.availability_id,
        slot_start: slot.start,
      });
      toast.success("Appuntamento spostato");
      notifyChange(); onSaved?.(); onClose();
    } catch (e) {
      toast.error(apiError(e, "Impossibile spostare l'appuntamento"));
    } finally {
      setMoving(false);
    }
  };

  const checkio = async (kind) => {
    try {
      await api.post(`/appointments/${editing.id}/${kind}`);
      toast.success(kind === "checkin" ? "Check-in registrato" : "Check-out registrato");
      notifyChange(); onSaved?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  const availablePhysios = useMemo(() => {
    const all = avail.physios || [];
    if (editing && !medicalEditing) return all.filter((p) => p.category !== "medico" || p.id === editing.physio_id);
    return all.filter((p) => p.category !== "medico");
  }, [avail.physios, editing, medicalEditing]);
  const machines = avail.machines || [];
  const endPreview = medicalSlot?.end || editing?.end || avail.end;
  const medicalDoctor = medicalSlot?.doctor || (medicalEditing ? { first_name: editing.physio_name || "Medico", last_name: "", color: editing.physio_color, specialization: editing.specialization || "" } : null);
  const readOnlyMedicalSchedule = medicalEditing;
  const medicalMetadataLocked = medicalEditing && editing?.status === "annullato";
  const canMoveMedical = medicalEditing && !["annullato", "completato", "non_presentato"].includes(editing?.status);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto rounded-2xl" data-testid="appointment-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">{editing ? "Gestisci appuntamento" : isMedical ? "Prenota slot medico" : "Nuovo appuntamento"}</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-2" data-testid="appointment-copy-paste-actions">
          <Button
            type="button"
            variant="outline"
            disabled={!editing || medicalEditing}
            onClick={copyCurrentAppointment}
            className={`rounded-xl ${editing && !medicalEditing ? "border-blue-200 text-blue-700 bg-blue-50/60" : "opacity-40"}`}
            data-testid="appointment-copy-btn"
          >
            <Copy size={15} className="mr-2" /> Copia
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={Boolean(editing) || !copiedAppointment || isMedical || saving}
            onClick={pasteCopiedAppointment}
            className={`rounded-xl ${!editing && copiedAppointment && !isMedical ? "border-emerald-200 text-emerald-700 bg-emerald-50/70" : "opacity-40"}`}
            data-testid="appointment-paste-btn"
          >
            <ClipboardPaste size={15} className="mr-2" /> Incolla
          </Button>
        </div>

        {editing && (
          <div className="flex flex-wrap items-center gap-2 bg-slate-50 rounded-xl p-3" data-testid="appointment-actions">
            <span className={`text-xs px-2.5 py-1 rounded-full border font-medium ${(STATUSES[editing.status] || {}).cls}`}>{(STATUSES[editing.status] || {}).label}</span>
            {!editing.checkin_at && !["completato", "annullato"].includes(editing.status) && (
              <Button size="sm" data-testid="appointment-checkin-btn" onClick={() => checkio("checkin")} className="rounded-full bg-emerald-600 hover:bg-emerald-700"><LogIn size={14} className="mr-1" /> Check-in</Button>
            )}
            {editing.checkin_at && !editing.checkout_at && (
              <Button size="sm" data-testid="appointment-checkout-btn" onClick={() => checkio("checkout")} className="rounded-full bg-blue-600 hover:bg-blue-700"><LogOut size={14} className="mr-1" /> Check-out</Button>
            )}
            {medicalEditing && editing.status === "annullato" ? (
              <Button size="sm" variant="outline" data-testid="appointment-reactivate-btn" onClick={reactivate} className="rounded-full"><RotateCcw size={14} className="mr-1" /> Riattiva slot</Button>
            ) : (
              <Select value={editing.status} onValueChange={setStatus}>
                <SelectTrigger data-testid="appointment-status-select" className="w-44 rounded-xl h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUSES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {!medicalEditing && <Button size="sm" variant="ghost" data-testid="appointment-delete-btn" onClick={del} className="text-red-500 ml-auto"><Trash2 size={14} /></Button>}
          </div>
        )}

        {isMedical && (
          <div className="rounded-2xl border p-4" style={{ borderColor: `${medicalDoctor?.color || "#60A5FA"}55`, background: `${medicalDoctor?.color || "#60A5FA"}12` }} data-testid="medical-slot-summary">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full" style={{ background: medicalDoctor?.color || "#60A5FA" }} />
              <span className="font-semibold text-sm">{medicalEditing ? editing.physio_name : `${medicalDoctor?.first_name || ""} ${medicalDoctor?.last_name || ""}`.trim()}</span>
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Medico / specialista</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{medicalSlot?.doctor?.specialization || editing?.specialization || ""}</p>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <div className="rounded-xl bg-white/80 px-2 py-2"><p className="text-[10px] uppercase text-slate-400">Data</p><p className="text-sm font-medium">{new Date(date + "T12:00:00").toLocaleDateString("it-IT")}</p></div>
              <div className="rounded-xl bg-white/80 px-2 py-2"><p className="text-[10px] uppercase text-slate-400">Orario</p><p className="text-sm font-medium">{start}–{endPreview}</p></div>
              <div className="rounded-xl bg-white/80 px-2 py-2"><p className="text-[10px] uppercase text-slate-400">Durata</p><p className="text-sm font-medium">{duration} min</p></div>
            </div>
            {medicalEditing && <p className="text-xs text-slate-500 mt-2">Orario, durata e paziente restano vincolati allo slot. I dati della visita possono essere aggiornati qui sotto.</p>}
          </div>
        )}

        {canMoveMedical && (
          <div className="rounded-2xl border border-violet-100 bg-violet-50/40 p-4 space-y-3" data-testid="medical-move-panel">
            <div className="flex items-center gap-2">
              <CalendarClock size={16} className="text-violet-600" />
              <div>
                <p className="text-sm font-semibold">Sposta appuntamento</p>
                <p className="text-xs text-slate-500">Scegli una data e poi uno slot libero dello stesso medico.</p>
              </div>
            </div>
            <Input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)} className="rounded-xl" data-testid="medical-move-date" />
            {moveLoading ? (
              <p className="text-xs text-slate-400">Caricamento slot...</p>
            ) : moveOptions.length ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="medical-move-options">
                {moveOptions.map((slot) => (
                  <button key={`${slot.availability_id}-${slot.start}`} type="button" disabled={moving} onClick={() => moveTo(slot)}
                    className="rounded-xl border border-violet-200 bg-white px-3 py-2 text-left hover:border-violet-400 disabled:opacity-50">
                    <span className="block font-mono text-xs font-semibold">{slot.start}–{slot.end}</span>
                    <span className="text-[10px] text-slate-400">{slot.duration} min</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-xs text-slate-400">Nessuno slot libero per questa data.</p>
            )}
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label>Paziente *</Label>
            <PatientSelect value={patient} onChange={readOnlyMedicalSchedule ? undefined : setPatient} disabled={readOnlyMedicalSchedule} />
          </div>

          {!isMedical && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Data *</Label>
                  <Input data-testid="appointment-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-xl" />
                </div>
                <div>
                  <Label>Ora inizio *</Label>
                  <Input data-testid="appointment-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} className="rounded-xl" />
                </div>
                <div>
                  <Label>Durata</Label>
                  <Select value={String(duration)} onValueChange={(v) => setDuration(Number(v))}>
                    <SelectTrigger data-testid="appointment-duration" className="rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DURATIONS.map((d) => <SelectItem key={d} value={String(d)}>{d} min</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {endPreview && <p className="text-xs text-slate-500" data-testid="appointment-end-preview">Fine prevista: {endPreview}</p>}
            </>
          )}

          <div>
            <Label>Trattamento</Label>
            <Select value={treatmentId} onValueChange={onTreatment} disabled={medicalMetadataLocked}>
              <SelectTrigger data-testid="appointment-treatment" className="rounded-xl"><SelectValue placeholder="Seleziona trattamento" /></SelectTrigger>
              <SelectContent>
                {treatments.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} — €{t.price}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {!isMedical && (
            <div>
              <Label>Fisioterapista *</Label>
              <div className="grid grid-cols-1 gap-1.5 mt-1" data-testid="appointment-physio-list">
                {availablePhysios.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`appointment-physio-${p.id}`}
                    disabled={!p.available && physioId !== p.id}
                    onClick={() => setPhysioId(p.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all ${
                      physioId === p.id ? "border-blue-500 bg-blue-50" : p.available ? "border-slate-200 hover:border-blue-300" : "border-slate-100 opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <span className="w-3 h-3 rounded-full" style={{ background: p.color || "#2563EB" }} />
                    <span className="font-medium text-sm">{p.first_name} {p.last_name}</span>
                    <span className={`ml-auto text-xs ${p.available ? "text-emerald-600" : "text-red-500"}`}>{p.available ? "Disponibile" : p.reason}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {!medicalMetadataLocked && machines.length > 0 && (
            <div>
              <Label>Macchinario (opzionale)</Label>
              <div className="grid grid-cols-1 gap-1.5 mt-1" data-testid="appointment-machine-list">
                {machines.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`appointment-machine-${m.id}`}
                    disabled={!m.available && machineId !== m.id}
                    onClick={() => { setMachineId(machineId === m.id ? "" : m.id); setAutoAssign(false); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all ${
                      machineId === m.id ? "border-cyan-500 bg-cyan-50" : m.available ? "border-slate-200 hover:border-cyan-300" : "border-slate-100 opacity-50 cursor-not-allowed"
                    }`}
                  >
                    <span className="font-medium text-sm">{m.name}</span>
                    <span className="text-xs text-slate-400">{m.type_name}{m.effective_requires_operator ? " · con operatore" : ""}</span>
                    <span className={`ml-auto text-xs ${m.available ? "text-emerald-600" : "text-red-500"}`}>{m.available ? "Disponibile" : m.reason}</span>
                  </button>
                ))}
                <label className="flex items-center gap-2 px-1 pt-1 text-sm text-slate-600">
                  <Checkbox data-testid="appointment-auto-machine" checked={autoAssign} onCheckedChange={(c) => { setAutoAssign(Boolean(c)); if (c) setMachineId(""); }} />
                  Assegna automaticamente la prima disponibile
                </label>
              </div>
            </div>
          )}

          <div>
            <Label>Note appuntamento</Label>
            <Textarea data-testid="appointment-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={medicalMetadataLocked} rows={2} className="rounded-xl" placeholder="Es. Controllare mobilità spalla destra" />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Prezzo previsto (€)</Label>
              <Input data-testid="appointment-price" type="number" min="0" step="0.5" value={price} onChange={(e) => setPrice(e.target.value)} disabled={medicalMetadataLocked} className="rounded-xl" />
            </div>
            <div>
              <Label>Pagamento</Label>
              <Select value={payStatus} onValueChange={setPayStatus} disabled={medicalMetadataLocked}>
                <SelectTrigger data-testid="appointment-pay-status" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="da_pagare">Da pagare</SelectItem>
                  <SelectItem value="pagato">Pagato</SelectItem>
                  <SelectItem value="parziale">Parziale</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Metodo</Label>
              <Select value={payMethod} onValueChange={setPayMethod} disabled={medicalMetadataLocked}>
                <SelectTrigger data-testid="appointment-pay-method" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contanti">Contanti</SelectItem>
                  <SelectItem value="carta/POS">Carta/POS</SelectItem>
                  <SelectItem value="bonifico">Bonifico</SelectItem>
                  <SelectItem value="altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" data-testid="appointment-cancel-btn" onClick={onClose} className="rounded-full">{medicalEditing ? "Chiudi" : "Annulla"}</Button>
          {(!medicalEditing || !medicalMetadataLocked) && (
            <Button data-testid="appointment-save-btn" onClick={save} disabled={saving} className="rounded-full">
              {saving ? "Salvataggio..." : medicalEditing ? "Salva dati" : editing ? "Salva modifiche" : isMedical ? "Prenota slot" : "Crea appuntamento"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
